import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import React, { useEffect, useState } from 'react';
import useEnhancedAuth from '../EnhancedAuth';
import './Default.css';
import { apiGetUser, apiSaveUser } from '../services/api';
import AppPageHeader from '../components/AppPageHeader';
import { returnToMain } from '../utils/nav';
import { useTranslation } from 'react-i18next';
import AccountPersonalForm from '../usecases/account/AccountPersonalForm';
import {
  clearPhoneNumber,
  strIsEmail,
  strIsPhone,
  strIsValidName,
} from '../utils/strings';
import {
  getAppError,
  getAppSuccess,
  getUserMessageOrValidationError,
} from '../utils/messages';
import { cfgFirstNameMinLength, cfgLastNameMinLength } from '../utils/config';
import { useHistory } from 'react-router';
import { LoadingController } from '../utils/LoadingController';
import { useToaster } from '../hooks/useToaster';

const AccountPage: React.FC = () => {
  const auth = useEnhancedAuth();
  const [t] = useTranslation('global');
  const router = useHistory();
  const [present] = useToaster();

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [email, setEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [phone, setPhone] = useState<string>('');

  const [isPageActive, setIsPageActive] = useState(false);
  useIonViewDidEnter(() => {
    setIsPageActive(true);
  });
  useIonViewWillLeave(() => {
    setIsPageActive(false);
  });

  const setClearPhone = (p: string) => {
    setPhone(clearPhoneNumber(p));
  }

  const loadDataFromApi = async () => {
    LoadingController.show();
    await apiGetUser(auth)
      .then((response) => {
        setEmail(response.data.email);
        setFirstName(response.data.firstName);
        setLastName(response.data.lastName);
        setClearPhone(response.data.phoneNumber);
      })
      .catch((e) => {
        console.log('Error occurred while retrieving data', e);
      })
      .finally(() => {
        LoadingController.hide();
      });
  };

  useEffect(() => {
    loadDataFromApi().then(() => console.log('Load account complete'));
  }, [auth.isLoading]);

  const refresh = (e: CustomEvent) => {
    loadDataFromApi().then(() => e.detail.complete());
  };

  const getPersonalValidationError = () => {
    if (!strIsValidName(firstName, cfgFirstNameMinLength())) {
      return t('register.errorfirstname');
    }
    if (!strIsValidName(lastName, cfgLastNameMinLength())) {
      return t('register.errorlastname');
    }
    if (!strIsEmail(email)) {
      return t('register.erroremail');
    }
    if (!strIsPhone(phone)) {
      return t('register.errorphone');
    }
    return false;
  };

  const saveChanges = async () => {
    if (isSubmitting) {
      return;
    }

    const validationError = getPersonalValidationError();
    if (validationError !== false) {
      present(getAppError(validationError));
      return;
    }

    try {
      LoadingController.show();
      setIsSubmitting(true);
      await apiSaveUser(
        {
          email: email,
          firstName: firstName,
          lastName: lastName,
          phoneNumber: phone,
        },
        auth,
      );
      present(getAppSuccess(t('account.success')));
    } catch (error) {
      console.error('Error registering user ', error);
      const apiError = getUserMessageOrValidationError(error);
      if (typeof apiError === 'string') {
        present(getAppError(apiError));
      } else {
        present(getAppError(t('account.nosuccess')));
      }
    } finally {
      setIsSubmitting(false);
      LoadingController.hide();
    }
  };

  return (
    <IonPage id="account-page">
      <AppPageHeader
        isPageActive={isPageActive}
        back={() => returnToMain(router)}
        title={t('account.title')}
      ></AppPageHeader>
      <IonContent fullscreen className="ion-padding default-content">
        <IonRefresher slot="fixed" onIonRefresh={refresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>
        <AccountPersonalForm
          isValid={getPersonalValidationError() === false}
          email={email}
          setEmail={setEmail}
          proceed={saveChanges}
          setFirstName={setFirstName}
          setLastName={setLastName}
          setPhone={setClearPhone}
          firstName={firstName}
          lastName={lastName}
          phone={phone}
        ></AccountPersonalForm>
      </IonContent>
    </IonPage>
  );
};

export default AccountPage;
